import { Switch } from "../../compose/controls";
import { CAPABILITIES, MATRIX_ROLES } from "../../lib/roles";
import type { PermissionMatrix as MatrixData } from "../../api/types";

/**
 * The permission MATRIX — the answer to "it's all dropdowns". A real grid of
 * roles (rows) × capabilities (columns), each cell a toggle Switch (NOT a
 * dropdown). Toggling a cell flips the role's capability for the workspace; the
 * server (`AdminOps` → `authz.policy`) is authoritative and re-evaluates at the
 * route boundary. The `owner` row is locked-on (rendered as a fixed ✓). A cell
 * that differs from the static default is badged "customised", with a per-row
 * "Reset" that drops the overlay rows.
 *
 * Presentation only: all state + persistence lives in the parent (Admin screen),
 * which does the optimistic toggle + revert-on-failure.
 */
export function PermissionMatrix({
  matrix,
  canManage,
  busyCell,
  onToggle,
  onResetRole,
}: {
  matrix: MatrixData;
  /** The viewer holds `manage_permissions` — otherwise the grid is read-only. */
  canManage: boolean;
  /** The `role:capability` currently being persisted (shows a pending state). */
  busyCell?: string | null;
  onToggle: (role: string, capability: string, allowed: boolean) => void;
  onResetRole: (role: string) => void;
}) {
  const roleLabel = (key: string) =>
    MATRIX_ROLES.find((r) => r.key === key)?.label ?? key;

  return (
    <div className="perm-matrix-wrap" style={{ overflowX: "auto" }}>
      <table
        className="perm-matrix"
        data-testid="permission-matrix"
        role="grid"
        // ⛔ USER-VISIBLE COPY. An accessible name is read aloud verbatim, so it
        // is held to the same bar as a heading — and "matrix" is the internal
        // name for this grid (the word a client read in "Could not load the
        // matrix" and answered "what matrix?!"). That noun was scrubbed from
        // every string a sighted reader sees and survived HERE, where only a
        // screen-reader user meets it. The name now says what the tab that opens
        // this screen is called, plus what the screen answers.
        aria-label="Permissions — what each role can do"
      >
        <thead>
          <tr>
            <th className="perm-matrix__corner" scope="col">
              Role
            </th>
            {CAPABILITIES.map((c) => (
              <th key={c.key} scope="col" title={c.hint}>
                {c.label}
              </th>
            ))}
            <th scope="col" aria-label="Reset" />
          </tr>
        </thead>
        <tbody>
          {matrix.roles.map((row) => (
            <tr key={row.role} data-testid={`perm-row-${row.role}`}>
              <th scope="row" className="perm-matrix__role">
                {roleLabel(row.role)}
                {row.role === "owner" ? (
                  <span className="perm-matrix__lock" title="Owner is locked-on">
                    {" "}
                    🔒
                  </span>
                ) : null}
              </th>
              {row.cells.map((cell) => {
                const key = `${row.role}:${cell.capability}`;
                const customised = !cell.is_default;
                return (
                  <td
                    key={cell.capability}
                    className={`perm-matrix__cell${customised ? " perm-matrix__cell--custom" : ""}`}
                  >
                    <Switch
                      checked={cell.allowed}
                      disabled={!canManage || cell.locked || busyCell === key}
                      label={`${row.role} ${cell.capability}`}
                      data-testid={`perm-cell-${row.role}-${cell.capability}`}
                      data-allowed={cell.allowed ? "1" : "0"}
                      data-custom={customised ? "1" : "0"}
                      onChange={(next) =>
                        onToggle(row.role, cell.capability, next)
                      }
                    />
                  </td>
                );
              })}
              <td className="perm-matrix__reset">
                {row.role !== "owner" && canManage ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    data-testid={`perm-reset-${row.role}`}
                    onClick={() => onResetRole(row.role)}
                  >
                    Reset
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
